import React, { useEffect, useMemo, useState } from "react";
import WidgetCard from "@/components/controls/WidgetCard";
import { useTranslation } from "react-i18next";
import { capitalize } from "lodash";
import { UserData } from "@/entities/users/types";
import { SalesVsQuotaData } from "@/entities/reporting/types";
import Text from "@/components/ui/text";
import ComparisonChart, {
  ComparisonChartProps
} from "@/components/graphs/ComparisonChart/ComparisonChart";
import MultiColumnBarChart from "@/components/graphs/WinRate/MultiColumnBarChart";
import { PeerComparisonResult } from "@/components/layouts/LeadAnalysis/types";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { Bullet, BulletStriped, InfoCircle } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import AnnualSalesVSQuota from "@/components/layouts/SalesVSQuota/AnnualSalesVSQuota";
import { extractPeerComparison } from "@/components/layouts/SalesVSQuota/helpers";
import { formatNumber, reducePrecision } from "@/utils/numbers";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { simpleDate } from "@/utils/datetime";
import { cn } from "@/utils/utils";

export type SalesVSQuotaPropsType = {
  data?: SalesVsQuotaData;
  userData?: UserData;
  isLoading?: boolean;
};

export const getIsCompactWidget = (data?: SalesVsQuotaData): boolean => {
  const widgets = data?.salesVsQuota;
  if (!widgets) return false;

  // Return true for annual or quarterly widgets (compact size)
  return !!(
    widgets.annualQuotaWidget?.[0] || widgets.quarterlyQuotaWidget?.[0]
  );
};

const SalesVSQuota: React.FC<SalesVSQuotaPropsType> = ({
  userData,
  isLoading,
  data
}) => {
  const { t } = useTranslation();
  const showIndustryValue = useFeatureFlag(FeatureFlags.INDUSTRY_VALUE).enabled;
  const [peerComparison, setPeerComparison] = useState<PeerComparisonResult>();

  const overallData = data?.salesVsQuota?.overall;

  const mergedComparisonChartProps = useMemo(() => {
    if (peerComparison) {
      return {
        datasets: [
          {
            name: t(`comparison.titles.you`),
            value: reducePrecision(peerComparison.you?.value || 0)
          },
          {
            name: t(`comparison.titles.team`),
            value: reducePrecision(peerComparison.team.value || 0)
          },
          {
            name: t(`comparison.titles.organization`),
            value: reducePrecision(peerComparison.org.value || 0)
          },
          {
            name: t(`comparison.titles.industry`),
            value: peerComparison
              ? reducePrecision(peerComparison.industry?.value || 0)
              : 0
          }
        ],
        userData
      } as ComparisonChartProps;
    }
  }, [peerComparison]);

  useEffect(() => {
    if (data) {
      const newPeerComparison = extractPeerComparison(data);
      if (newPeerComparison) {
        setPeerComparison(newPeerComparison);
      }
    }
  }, [data]);

  if (!data) return null;

  const formatSalesData = (value: number) => {
    const formatted = formatNumber(value, 2, true);
    return `${formatted.currencySign}${formatted.value}${formatted.modifier}`;
  };

  const getWidgetStartDate = () => {
    const widgets = data?.salesVsQuota;
    if (!widgets) return null;

    if (widgets.annualQuotaWidget?.[0]) {
      const widget = widgets.annualQuotaWidget[0];
      return widget.startYear && widget.startMonth
        ? new Date(widget.startYear, widget.startMonth - 1, 1)
        : null;
    }

    if (widgets.monthlyQuotaWidget?.[0]) {
      const widget = widgets.monthlyQuotaWidget[0];
      return widget.year && widget.month
        ? new Date(widget.year, widget.month - 1, 1)
        : null;
    }

    if (widgets.quarterlyQuotaWidget?.[0]) {
      const widget = widgets.quarterlyQuotaWidget[0];
      return widget.startYear && widget.startMonth
        ? new Date(widget.startYear, widget.startMonth - 1, 1)
        : null;
    }

    return null;
  };

  const isCompactWidget = () => {
    const widgets = data?.salesVsQuota;
    if (!widgets) return false;

    // Return true for annual or quarterly widgets (compact size)
    return !!(
      widgets.annualQuotaWidget?.[0] || widgets.quarterlyQuotaWidget?.[0]
    );
  };

  const renderQuotaOverall = () => {
    const actualSalesValue =
      overallData?.yearlySalesVsQuota?.you?.actualSales?.value || 0;
    const quotaValue = overallData?.yearlySalesVsQuota?.you?.quota?.value || 0;

    return (
      <div className="flex items-baseline whitespace-nowrap -ml-2">
        <Text variant="lgSubtitleH1">{formatSalesData(actualSalesValue)}</Text>
        <Text variant="lgSubtitleH3" className="ml-1">
          {`/ ${formatSalesData(quotaValue)}`}
        </Text>
      </div>
    );
  };

  const renderQuotaAchievementTooltip = () => {
    const startDate = getWidgetStartDate();
    if (!startDate) return null;

    const actualSalesValue =
      overallData?.yearlySalesVsQuota?.you?.actualSales?.value || 0;
    const quotaValue = overallData?.yearlySalesVsQuota?.you?.quota?.value || 0;

    const quotaAchievementPercentage =
      quotaValue > 0 ? Math.round((actualSalesValue / quotaValue) * 100) : 0;

    return (
      <div className="flex flex-col gap-2 min-w-48">
        <Text variant="bodySecondary">
          {`${simpleDate(startDate, "short", false, true)} - ${capitalize(t("today"))}`}
        </Text>
        <div className="flex justify-between items-center">
          <Text>{formatSalesData(actualSalesValue)}</Text>
          <Text variant="bodySecondary">{t("salesVSQuota.actualSales")}</Text>
        </div>
        <div className="flex justify-between items-center">
          <Text>{formatSalesData(quotaValue)}</Text>
          <Text variant="bodySecondary">{t("salesVSQuota.quota")}</Text>
        </div>
        <div className="flex justify-between items-center">
          <Text>{`${quotaAchievementPercentage}%`}</Text>
          <Text variant="bodySecondary">
            {t("salesVSQuota.quotaAchievement")}
          </Text>
        </div>
      </div>
    );
  };

  const validPeerComparison =
    peerComparison &&
    peerComparison.team.value !== null &&
    peerComparison.org.value !== null;

  return (
    <WidgetCard
      cardHeight={isCompactWidget() ? "472px" : "29rem"}
      title={t("salesVSQuota.title")}
      isLoading={isLoading}
      showFeedBackButton
      // TODO: pass correct info
      infoTooltip={t("salesVSQuota.info")}
      feedBackButtonIdContext="sales-vs-quota"
      cardClassName="gap-6"
    >
      <div className="flex h-full w-full">
        <div className="flex flex-col gap-6 w-full h-full">
          <div className="py-1 flex gap-2">
            <div className="flex items-center">
              <IconContainer size="5" className="p-[5px] text-charts-grey-bar">
                <Bullet />
              </IconContainer>
              <Text variant="bodySecondary">{t("salesVSQuota.quota")}</Text>
            </div>
            <div className="flex items-center">
              <IconContainer size="5" color="cta" className="p-[5px]">
                <Bullet />
              </IconContainer>
              <Text variant="bodySecondary">
                {t("salesVSQuota.actualSales")}
              </Text>
            </div>
            <div className="flex items-center">
              <IconContainer size="5" color="cta" className="p-[5px]">
                <BulletStriped />
              </IconContainer>
              <Text variant="bodySecondary">
                {t("salesVSQuota.committedDeals")}
              </Text>
            </div>
          </div>
          {data?.salesVsQuota &&
          (data.salesVsQuota.monthlyQuotaWidget ||
            data.salesVsQuota.quarterlyQuotaWidget) ? (
            <MultiColumnBarChart
              height={250}
              chartData={
                data.salesVsQuota.monthlyQuotaWidget ??
                data.salesVsQuota.quarterlyQuotaWidget ??
                []
              }
              tooltipYpos={20}
              isPercentage={false}
              chartClassName="-translate-x-[50px]"
              legendClassName="-bottom-7"
              barWidth={12}
            />
          ) : (
            data.salesVsQuota.annualQuotaWidget && (
              <AnnualSalesVSQuota data={data.salesVsQuota.annualQuotaWidget} />
            )
          )}
        </div>
        {mergedComparisonChartProps && (
          <div
            className={cn(
              "flex flex-col h-72 w-[12.5rem]",
              `justify-${validPeerComparison ? "end" : "start"}`
            )}
          >
            <div className="flex flex-col gap-1 mb-6">
              <div className="flex gap-2 items-center">
                <Text variant="bodySecondary">
                  {t("salesVSQuota.quotaAchievementYTD")}
                </Text>
                <Tooltip>
                  <TooltipTrigger>
                    <IconContainer color="tertiary">
                      <InfoCircle />
                    </IconContainer>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-56">
                    {t("salesVSQuota.ytdInfo")}
                  </TooltipContent>
                </Tooltip>
              </div>
              <Tooltip>
                <TooltipTrigger className="w-fit">
                  <Text
                    variant="title1H1"
                    className="w-fit rounded-lg transition-all duration-200 ease-in-out p-1 text-start hover:bg-white-4"
                  >
                    {`${reducePrecision(overallData?.quotaAchievement?.you || 0)}%`}
                  </Text>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {renderQuotaAchievementTooltip()}
                </TooltipContent>
              </Tooltip>
              <div className="flex p-1">{renderQuotaOverall()}</div>
            </div>
            {validPeerComparison && (
              <>
                <Text variant="bodySecondary" className="mb-1">
                  {t("salesVSQuota.peerComparison")}
                </Text>
                <ComparisonChart
                  {...mergedComparisonChartProps}
                  hideIndustry={!showIndustryValue}
                  showCurrencySign={false}
                  unit="%"
                />
              </>
            )}
          </div>
        )}
      </div>
    </WidgetCard>
  );
};

export default SalesVSQuota;
